import axios, { AxiosRequestConfig } from "axios";
import environment from "../constants/Environment";
import { router } from "@/App";
import { hashPayload } from "@/utils/hashPayload";

const { SERVER_API_URL } = environment();

export const axiosInstance = axios.create({
  baseURL: SERVER_API_URL,
  withCredentials: true,
  // timeout: 15000, // 15 second timeout — don't hang forever on failed requests
});

axiosInstance.interceptors.request.use(function (response) {
  response.headers["token"] = window.localStorage.getItem("omni-token");

  return response;
});

// Prevent multiple 401/403 responses from racing to trigger logout
let isRedirectingToLogout = false;

axiosInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const pathname = router.window?.location.pathname ?? "";
    const disableRoute = [
      "/login",
      "/logout",
      "/templates",
      "/signup",
      "/dev-login",
    ];

    const skipLogoutUrls = [
      "/event/progress",
      "/integration/googlepicker/open-picker",
    ];

    if (
      !isRedirectingToLogout &&
      !disableRoute.includes(pathname) &&
      !skipLogoutUrls.includes(error.config?.url)
    ) {
      switch (error.response?.status) {
        case 401: {
          isRedirectingToLogout = true;
          router.navigate("/logout");
          break;
        }
        case 403: {
          const mu = window.localStorage.getItem("mu");
          if (mu === "true") break;

          isRedirectingToLogout = true;
          router.navigate("/logout");
          break;
        }
      }
    }

    return Promise.reject(error);
  },
);

export async function axiosGet(url: string, config: AxiosRequestConfig = {}) {
  return axiosInstance.get(url, config);
}

export async function axiosPost(
  url: string,
  data: Object = {},
  config: AxiosRequestConfig = {},
) {
  return axiosInstance.post(url, data, config);
}

export async function axiosHashedPost(
  url: string,
  payload: any,
  userId: string,
  companyId: string,
  signal?: AbortSignal,
) {
  const requestHash = await hashPayload(payload, userId, companyId);
  const response = await axiosInstance.post(url, payload, {
    headers: {
      "request-hash": requestHash,
    },
    signal,
  });

  const responseHash = await hashPayload(response.data, userId, companyId);
  if (response.headers["response-hash"] !== responseHash) {
    throw new Error("Data checksum mismatch");
  }
  return response;
}

export async function axiosDelete(
  url: string,
  config: AxiosRequestConfig = {},
) {
  return axiosInstance.delete(url, config);
}

export async function axiosPut(
  url: string,
  data: Object = {},
  config: AxiosRequestConfig = {},
) {
  return axiosInstance.put(url, data, config);
}

export async function axiosPatch(
  url: string,
  data: Object = {},
  config: AxiosRequestConfig = {},
) {
  return axiosInstance.patch(url, data, config);
}
